import { isNativeApp, isBundledNativeOrigin, resolveRuntimeOrigin } from './appOrigin';

/**
 * The header that tells the API a request came from the packaged app.
 *
 * The API keys the Stripe return marker off this: with it, `success_url` comes back as
 * `…/payment/success?session_id=…&client=capacitor-ios`, and the website's success page
 * bounces the buyer into the app. Without it, the API produces the plain web URL and the
 * buyer finishes on the website. See docs/api-app-checkout-return-requirement.md.
 *
 * ## The values are exact
 *
 * The API resolves an unrecognised value to "unknown" and returns an **unmarked** URL. So a
 * typo — `capacitor_ios`, `ios`, a stray build-time interpolation — does not error anywhere.
 * It silently produces the web behaviour, which looks identical to a working web build and
 * is invisible until someone notices app buyers are never coming back. Casing and
 * surrounding whitespace are tolerated server-side; genuinely different strings are not.
 * The API logs `LmsClient: unrecognised X-LMS-Client header` when one arrives, which is the
 * fastest way to confirm a suspicion.
 */
export const NATIVE_CLIENT_HEADER = 'X-LMS-Client';

const IOS_CLIENT = 'capacitor-ios';
const ANDROID_CLIENT = 'capacitor-android';

/**
 * Which packaged app this is, or `null` on the web.
 *
 * Gated on the *bundled* origin for the same reason as everything else in `appOrigin.ts`:
 * the Phase 0 diagnostic runs the Capacitor bridge against the real website, where web
 * behaviour is what is wanted.
 *
 * Platform comes from the bridge's own `getPlatform()`, with the origin as a fallback —
 * iOS serves bundled assets from `capacitor://localhost` and Android from
 * `https://localhost`, which is the same distinction `isBundledNativeOrigin` relies on.
 * The fallback exists because the failure mode above is silent: if `getPlatform` were ever
 * absent, sending no header at all would quietly turn every app purchase back into a web
 * one, and nothing would say so.
 *
 * Returns `null` rather than guessing when the platform is neither, since a wrong value is
 * worse than none — the API rejects it and produces the same unmarked URL, minus any
 * chance of the value being right.
 */
export function resolveNativeClient(): string | null {
   if (!isNativeApp() || !isBundledNativeOrigin()) return null;

   const platform = readBridgePlatform() || platformFromOrigin();

   if (platform === 'ios') return IOS_CLIENT;
   if (platform === 'android') return ANDROID_CLIENT;

   return null;
}

/**
 * Read from the injected `window.Capacitor` global rather than importing
 * `@capacitor/core`, so this module stays dependency-free and web builds never pull the
 * package in. Same reasoning as `isNativeApp()`.
 */
function readBridgePlatform(): string | null {
   if (typeof window === 'undefined') return null;

   const bridge = (window as unknown as { Capacitor?: { getPlatform?: () => string } }).Capacitor;

   if (typeof bridge?.getPlatform !== 'function') return null;

   return bridge.getPlatform();
}

function platformFromOrigin(): string | null {
   const origin = resolveRuntimeOrigin();

   if (origin.startsWith('capacitor://')) return 'ios';
   if (origin === 'https://localhost') return 'android';

   return null;
}
